import { ReactElement, useEffect, useState } from 'react';
import { ProgressButton, StatusIndicator } from '@localstack/ui';
import { useAwsEffect, useAwsGetter, useLocalstackStatus, useRegion, retrieveAuthToken } from '@localstack/services';
import { Box, Button, Chip, Grid, Step, StepContent, StepLabel, Stepper, Typography, styled } from '@mui/material';
import { GitHub, Info, OpenInNew } from '@mui/icons-material';

import { ExternalLink } from '@localstack/constants';
import { CloudPodMergeStrategy, LocalStackEvent } from '@localstack/types';

import { ContainedCustomerLayout } from '~/layouts';
import { AppRoute } from '~/config';

import { AlertLocalStackNotRunning, AlertLocalStackRunning } from '../../Launchpad/components';

const DEMO_SERVICES = ['Lambda', 'DynamoDB', 'ApiGateway', 'CloudFront', 'S3', 'SQS', 'SES', 'StepFunctions', 'SNS'];
const DEMO_REGION = 'us-east-1';
const DEMO_POD_APP = 'localstack-swag-store';
const DEMO_POD_APP_DATA = 'localstack-swag-store-data';
const ARCHITECTURE_IMAGE = '/localstack-swag-store/localstack-swag-store-diagram.png';

const StepperContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
});

const StepperMidsection = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.4rem',
  fontWeight: 500,
});

const StepperActions = styled(Box)({
  display: 'flex',
  gap: '1rem',
});

const StyledGridItem = styled(Grid)({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
});

const ArchSection = styled(Grid)({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  gap: '0.5rem',
});

const StepInfo = styled('span')(({ theme }) => ({
  fontSize: '0.9rem',
  verticalAlign: 'middle',
  color: theme.palette.text.secondary,
  '& svg': {
    height: '16px',
    verticalAlign: 'middle',
  },
}));

export const LocalStackSwagStore = (): ReactElement => {
  const { running, version } = useLocalstackStatus();
  const [showLeftColumn, setShowLeftColumn] = useState(true);
  const [hasLoadedPod, setHasLoadedPod] = useState(false);
  const [logOutput, setLogOutput] = useState<LocalStackEvent[]>([]);
  const [loadingPodName, setLoadingPodName] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  const { region, setRegion } = useRegion();

  useEffect(() => {
    if (region !== DEMO_REGION) setRegion(DEMO_REGION);
  }, []);

  const { data: distros, mutate: reloadDistros } = useAwsGetter('CloudFront', 'listDistributions', undefined, {
    silentErrors: true,
  });

  const {
    loadPod,
    isLoading: isLoadingPod,
    hasError: hasPodLoadingError,
  } = useAwsEffect('LocalStack', ['loadPod'], { silentErrors: true });

  const appCloudFrontDistroId = distros?.DistributionList?.Items?.[0]?.Id ?? 'swagapp';
  const appUrl = `https://localhost.localstack.cloud/cloudfront/${appCloudFrontDistroId}/`;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const onLoadPod = async (podName: string) => {
    setLogOutput([]);
    setHasLoadedPod(false);
    setLoadingPodName(podName);
    setLogOutput([{ event: 'status', message: 'Loading pod...' }]);
    const authToken = retrieveAuthToken();
    await loadPod(podName, undefined, authToken?.token, CloudPodMergeStrategy.ACCOUNT_REGION_MERGE);
    setHasLoadedPod(true);
    setLoadingPodName('');
    reloadDistros();
    handleNext();
  };

  const steps = [
    {
      label: 'Load infrastructure configuration',
      description: (
        <StepperContainer>
          <Typography>Deploy AWS services required to set up the application infrastructure.</Typography>
          <StepperMidsection>
            {DEMO_SERVICES.map((service) => (
              <Chip key={service} label={service} variant="outlined" size="small" />
            ))}
          </StepperMidsection>
          <StepperActions>
            <ProgressButton
              variant="contained"
              color="primary"
              disabled={loadingPodName === DEMO_POD_APP || !running}
              onClick={() => {
                onLoadPod(DEMO_POD_APP);
              }}
              loading={loadingPodName === DEMO_POD_APP}
            >
              Load Cloud Pod
            </ProgressButton>
            {(isLoadingPod || hasLoadedPod) && (
              <StatusIndicator
                isProgressing={isLoadingPod}
                logEvents={logOutput}
                hasError={hasPodLoadingError && !isLoadingPod}
              />
            )}
          </StepperActions>
          {!running && (
            <StepInfo>
              <Info /> You need a running LocalStack instance to load a cloud pod.
            </StepInfo>
          )}
        </StepperContainer>
      ),
    },
    {
      label: 'View deployed application',
      description: (
        <StepperContainer>
          <Typography>View the deployed application on your LocalStack instance.</Typography>
          <StepperMidsection>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setHasLoadedPod(false);
                handleNext();
              }}
            >
              Continue
            </Button>
            <Button variant="text" target="_blank" href={appUrl}>
              View application&nbsp;
              <OpenInNew />
            </Button>
          </StepperMidsection>
        </StepperContainer>
      ),
    },
    {
      label: 'Load application data',
      description: (
        <StepperContainer>
          <Typography>Insert swag store inventory and attendee data into DynamoDB.</Typography>
          <StepperMidsection>
            <Chip label="DynamoDB" variant="outlined" size="small" />
          </StepperMidsection>
          <StepperActions>
            <ProgressButton
              variant="contained"
              color="primary"
              disabled={loadingPodName === DEMO_POD_APP_DATA || !running}
              onClick={() => {
                onLoadPod(DEMO_POD_APP_DATA);
              }}
              loading={loadingPodName === DEMO_POD_APP_DATA}
            >
              Load Cloud Pod
            </ProgressButton>
            {(isLoadingPod || hasLoadedPod) && (
              <StatusIndicator
                isProgressing={isLoadingPod}
                logEvents={logOutput}
                hasError={hasPodLoadingError && !isLoadingPod}
              />
            )}
          </StepperActions>
          {!running && (
            <StepInfo>
              <Info /> You need a running LocalStack instance to load a cloud pod.
            </StepInfo>
          )}
        </StepperContainer>
      ),
    },
  ];

  return (
    <ContainedCustomerLayout
      title="LocalStack Swag Store deployed with Cloud Pods layers"
      breadcrumbs={[{ label: 'Quickstart', href: AppRoute.QUICKSTART }, { label: 'LocalStack Swag Store' }]}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {!running ? AlertLocalStackNotRunning : AlertLocalStackRunning(version)}
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <StyledGridItem
              item
              xs={12}
              md={5}
              sx={{ display: showLeftColumn ? 'flex' : 'none !important', borderRadius: '8px' }}
            >
              <Typography variant="h3">LocalStack Swag Store</Typography>
              <div>
                <Button
                  variant="text"
                  startIcon={<GitHub />}
                  href={ExternalLink.DEMO_SWAG_STORE}
                  target="_blank"
                  size="small"
                >
                  localstack-samples/localstack-swag-store
                </Button>
              </div>
              <Typography variant="body1">
                A full-stack e-commerce experience that powers the booth token redemption workflow. The Swag Store
                demonstrates how LocalStack emulates AWS services end-to-end for rapid development, testing, and demos.
              </Typography>
              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((step) => (
                  <Step key={step.label}>
                    <StepLabel>
                      <Typography variant="h5">{step.label}</Typography>
                    </StepLabel>
                    <StepContent>{step.description}</StepContent>
                  </Step>
                ))}
                {activeStep === 3 && (
                  <Typography variant="body1" style={{ marginLeft: '32px' }}>
                    <span>Application infrastructure and data loaded.</span>
                    <Button variant="text" target="_blank" href={appUrl}>
                      View application&nbsp;
                      <OpenInNew />
                    </Button>
                  </Typography>
                )}
              </Stepper>
            </StyledGridItem>
            <ArchSection item xs={12} md={showLeftColumn ? 7 : 12}>
              <Box
                onClick={() => {
                  setShowLeftColumn(!showLeftColumn);
                }}
                style={{ cursor: showLeftColumn ? 'zoom-in' : 'zoom-out' }}
              >
                <img alt="" src={ARCHITECTURE_IMAGE} width="100%" style={{ borderRadius: '16px' }} />
              </Box>
              <Typography variant="subtitle2">Application Architecture</Typography>
            </ArchSection>
          </Grid>
        </Grid>
      </Grid>
    </ContainedCustomerLayout>
  );
};
